import type { Server as HttpServer, IncomingMessage } from 'node:http';
import type { Duplex } from 'node:stream';
import WebSocket, { WebSocketServer } from 'ws';
import { metrics } from '../metrics.js';

/** Configuration for the hub streaming server. */
export interface HubStreamingServerOptions {
  /** HTTP server to mount WebSocket endpoints on. */
  server: HttpServer;
  /** Maximum frames per second to send to each live client. */
  maxFps: number;
}

/** Per-client state for frame throttling (live clients only). */
interface LiveClientState {
  ws: WebSocket;
  lastFrameSentAt: number;
}

/** Stored action from the current session. */
interface StoredAction {
  seq: number;
  tool: string;
  args: Record<string, unknown>;
  timestamp: string;
  pageUrl: string;
  pageTitle: string;
}

/**
 * Hub streaming server for the web-only pm2 instance.
 *
 * Mounts two WebSocket paths on the provided HTTP server:
 * - `/ws/ingest` — accepts relay connections from proxy instances
 * - `/ws/live` — serves UI clients (same protocol as StreamingServer)
 *
 * Tracks session state from ingest events and re-broadcasts to all
 * live clients with per-client frame throttling.
 */
export class HubStreamingServer {
  private readonly ingestWss: WebSocketServer;
  private readonly liveWss: WebSocketServer;
  private readonly ingestClients = new Set<WebSocket>();
  private readonly liveClients = new Set<LiveClientState>();
  private readonly frameIntervalMs: number;
  private readonly handleUpgrade: (req: IncomingMessage, socket: Duplex, head: Buffer) => void;
  private readonly server: HttpServer;

  private activeSessionId: string | null = null;
  private latestFrame: Buffer | null = null;
  private actions: StoredAction[] = [];

  constructor(options: HubStreamingServerOptions) {
    this.server = options.server;
    this.frameIntervalMs = Math.floor(1000 / options.maxFps);

    // Use noServer mode so we can route upgrades by path ourselves
    this.ingestWss = new WebSocketServer({ noServer: true });
    this.liveWss = new WebSocketServer({ noServer: true });

    this.ingestWss.on('connection', (ws) => this.handleIngestConnection(ws));
    this.liveWss.on('connection', (ws) => this.handleLiveConnection(ws));

    this.handleUpgrade = (req: IncomingMessage, socket: Duplex, head: Buffer) => {
      const pathname = req.url ?? '';

      if (pathname === '/ws/ingest') {
        this.ingestWss.handleUpgrade(req, socket, head, (ws) => {
          this.ingestWss.emit('connection', ws, req);
        });
      } else if (pathname === '/ws/live') {
        this.liveWss.handleUpgrade(req, socket, head, (ws) => {
          this.liveWss.emit('connection', ws, req);
        });
      } else {
        socket.destroy();
      }
    };

    this.server.on('upgrade', this.handleUpgrade);
  }

  /** Number of connected live (UI) clients. */
  get liveClientCount(): number {
    return this.liveClients.size;
  }

  /** Number of connected ingest (relay) clients. */
  get ingestClientCount(): number {
    return this.ingestClients.size;
  }

  /** Shut down both WebSocket servers and close all connections. */
  shutdown(): void {
    this.server.removeListener('upgrade', this.handleUpgrade);

    for (const client of this.liveClients) {
      client.ws.close();
    }
    this.liveClients.clear();

    for (const ws of this.ingestClients) {
      ws.close();
    }
    this.ingestClients.clear();

    this.liveWss.close();
    this.ingestWss.close();
  }

  private handleIngestConnection(ws: WebSocket): void {
    this.ingestClients.add(ws);

    ws.on('message', (raw: Buffer, isBinary: boolean) => {
      if (isBinary) {
        // Binary message = raw JPEG frame
        this.latestFrame = raw;
        this.broadcastFrameToLive(raw);
      } else {
        // Text message = JSON (session lifecycle, actions)
        const msg = JSON.parse(raw.toString()) as Record<string, unknown>;
        this.handleIngestMessage(msg);
      }
    });

    ws.on('close', () => {
      this.ingestClients.delete(ws);
    });

    ws.on('error', () => {
      this.ingestClients.delete(ws);
    });
  }

  private handleIngestMessage(msg: Record<string, unknown>): void {
    switch (msg.type) {
      case 'session_start': {
        this.activeSessionId = msg.sessionId as string;
        this.actions = [];
        this.latestFrame = null;
        this.broadcastToLive(msg);
        break;
      }

      case 'session_end': {
        this.activeSessionId = null;
        this.latestFrame = null;
        this.broadcastToLive(msg);
        break;
      }

      case 'action': {
        const action: StoredAction = {
          seq: msg.seq as number,
          tool: msg.tool as string,
          args: msg.args as Record<string, unknown>,
          timestamp: msg.timestamp as string,
          pageUrl: msg.pageUrl as string,
          pageTitle: msg.pageTitle as string,
        };
        this.actions.push(action);
        this.broadcastToLive(msg);
        break;
      }

      default:
        break;
    }
  }

  private handleLiveConnection(ws: WebSocket): void {
    const state: LiveClientState = { ws, lastFrameSentAt: 0 };
    this.liveClients.add(state);
    metrics.increment('wsConnectionsActive');
    metrics.increment('wsConnectionsTotal');

    ws.on('close', () => {
      this.liveClients.delete(state);
      metrics.decrement('wsConnectionsActive');
    });

    ws.on('error', () => {
      this.liveClients.delete(state);
      metrics.decrement('wsConnectionsActive');
    });

    // Send initial state
    if (this.activeSessionId) {
      this.send(ws, { type: 'session_start', sessionId: this.activeSessionId });

      if (this.latestFrame) {
        ws.send(this.latestFrame);
      }

      // Replay all actions from the current session
      for (const action of this.actions) {
        this.send(ws, {
          type: 'action',
          seq: action.seq,
          tool: action.tool,
          args: action.args,
          timestamp: action.timestamp,
          pageUrl: action.pageUrl,
          pageTitle: action.pageTitle,
        });
      }
    } else {
      this.send(ws, { type: 'idle' });
    }
  }

  private broadcastFrameToLive(frame: Buffer): void {
    const now = Date.now();

    for (const client of this.liveClients) {
      if (client.ws.readyState !== WebSocket.OPEN) continue;
      if (now - client.lastFrameSentAt < this.frameIntervalMs) continue;
      client.lastFrameSentAt = now;
      client.ws.send(frame);
    }
  }

  private broadcastToLive(msg: Record<string, unknown>): void {
    const raw = JSON.stringify(msg);
    for (const client of this.liveClients) {
      if (client.ws.readyState === WebSocket.OPEN) {
        client.ws.send(raw);
      }
    }
  }

  private send(ws: WebSocket, msg: Record<string, unknown>): void {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }
}
